if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      applyCartDiscount(discountCode, redirectPath) {
        if (!discountCode) {
          window.location = redirectPath;
          return Promise.resolve();
        }

        const rootPath =
          window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';

        return fetch(`${rootPath}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ discount: discountCode }),
        })
          .catch(() => null)
          .finally(() => {
            window.location = redirectPath;
          });
      }

      getBundleItems() {
        const bundleSection =
          this.form.querySelector('[data-bundle-offers]') || document.querySelector('[data-bundle-offers]');
        const activeOption = bundleSection?.querySelector('[data-bundle-option]:checked');
        const variantDataNode = bundleSection?.querySelector('[data-bundle-variant-prices]');
        const variantId = this.form.querySelector('[name="id"]')?.value;

        if (!bundleSection || !activeOption || !variantDataNode || !variantId) return null;

        let variantData = [];
        try {
          variantData = JSON.parse(variantDataNode.textContent || '[]');
        } catch (error) {
          variantData = [];
        }

        const currentVariant = variantData.find((variant) => String(variant.id) === String(variantId));
        if (!currentVariant || !Array.isArray(currentVariant.options)) return null;

        const quantity = Number(activeOption.dataset.quantity || 1);
        const colorOptionIndex = Number(bundleSection.dataset.colorOptionIndex);
        const activePanel = activeOption.parentElement?.querySelector('.product-bundle-offers__panel');
        const colorSelects = activePanel
          ? Array.from(activePanel.querySelectorAll('[data-bundle-item-color]'))
          : [];

        const lineItemMap = new Map();

        for (let index = 0; index < quantity; index += 1) {
          const desiredOptions = currentVariant.options.slice();

          if (Number.isInteger(colorOptionIndex) && colorOptionIndex >= 0 && colorSelects[index]) {
            desiredOptions[colorOptionIndex] = colorSelects[index].value;
          }

          const matchedVariant =
            variantData.find((variant) => {
              if (!Array.isArray(variant.options)) return false;
              return desiredOptions.every((value, optionIndex) => variant.options[optionIndex] === value);
            }) || currentVariant;

          const key = String(matchedVariant.id);
          const existingQuantity = lineItemMap.get(key) || 0;
          lineItemMap.set(key, existingQuantity + 1);
        }

        return Array.from(lineItemMap.entries()).map(([id, itemQuantity]) => ({
          id,
          quantity: itemQuantity,
        }));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const submitter = evt.submitter || this.submitButton;
        const submitButton = submitter || this.submitButton;
        const submitButtonText = submitButton?.querySelector('span') || this.submitButtonText;
        const loadingSpinner =
          submitButton?.querySelector('.loading__spinner') || this.querySelector('.loading__spinner');
        const isBuyNow = submitter && submitter.name === 'checkout';
        const shouldRedirectToCart = this.dataset.redirectToCart === 'true';

        if (submitButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        submitButton?.setAttribute('aria-disabled', true);
        submitButton?.classList.add('loading');
        loadingSpinner?.classList.remove('hidden');

        const formData = new FormData(this.form);
        const bundleDiscountCode = '';
        const bundleItems = this.getBundleItems();
        const shouldRenderCart = this.cart && !isBuyNow;
        const config = bundleItems && bundleItems.length > 0 ? fetchConfig() : fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (shouldRenderCart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (bundleItems && bundleItems.length > 0) {
          config.body = JSON.stringify({
            items: bundleItems,
            ...(shouldRenderCart
              ? {
                  sections: this.cart.getSectionsToRender().map((section) => section.id),
                  sections_url: window.location.pathname,
                }
              : {}),
          });
        } else {
          delete config.headers['Content-Type'];
          if (shouldRenderCart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        const addToCartUrl = bundleItems && bundleItems.length > 0 ? `${routes.cart_add_url}.js` : `${routes.cart_add_url}`;

        fetch(addToCartUrl, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = submitButton?.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              submitButton?.setAttribute('aria-disabled', true);
              submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (isBuyNow) {
              if (shouldRedirectToCart) {
                this.applyCartDiscount(bundleDiscountCode.trim(), window.routes.cart_url);
                return;
              }
              window.location = '/checkout';
              return;
            } else if (shouldRedirectToCart) {
              this.applyCartDiscount(bundleDiscountCode.trim(), window.routes.cart_url);
              return;
            } else if (!this.cart) {
              this.applyCartDiscount(bundleDiscountCode.trim(), window.routes.cart_url);
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            submitButton?.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) submitButton?.removeAttribute('aria-disabled');
            loadingSpinner?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
